/**
 * WeaveIn.ai Platform - Pricing Proposals API Service
 * TypeScript service layer for pricing proposal management
 * Integrates with Migration 124: Pricing & Billing Proposals
 */

import { apiClient } from '../apiClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type PricingModel = 'per_service' | 'milestone_based' | 'hourly' | 'package';
export type ProposalType = 'initial' | 'counter' | 'final';
export type ProposalStatus = 'draft' | 'submitted' | 'accepted' | 'rejected' | 'expired' | 'superseded';
export type MilestoneStatus = 'pending' | 'in_progress' | 'completed' | 'verified' | 'paid';

export interface PricingMilestone {
  id?: number;
  order: number;
  name: string;
  description?: string;
  percentage: number;
  amount: number;
  criteria?: string;
  status?: MilestoneStatus;
  completed_at?: string;
  verified_at?: string;
}

export interface PricingAdjustment {
  type: string;
  description: string;
  amount: number;
}

export interface PricingProposal {
  id: number;
  proposal_number: string;
  service_referral_id: number;
  referral_number?: string;
  referral_match_id?: number;
  provider_type: string;
  provider_id: number;
  provider_name?: string;
  proposal_type: ProposalType;
  proposal_status: ProposalStatus;
  pricing_model: PricingModel;
  currency: string;
  base_rate?: number;
  estimated_units: number;
  estimated_total?: number;
  milestones: PricingMilestone[];
  hourly_rate?: number;
  estimated_hours?: number;
  max_hours?: number;
  adjustments: PricingAdjustment[];
  subtotal?: number;
  tax_amount: number;
  total_amount: number;
  previous_proposal_id?: number;
  negotiation_notes?: string;
  counter_reason?: string;
  valid_from?: string;
  valid_until?: string;
  expires_at?: string;
  responded_at?: string;
  response_notes?: string;
  created_at: string;
  updated_at: string;
  created_by?: number;
  negotiation_rounds?: number;
  urgency_status?: 'active' | 'expiring_soon' | 'expired';
}

export interface PricingMilestoneRecord {
  id: number;
  proposal_id: number;
  service_referral_id: number;
  milestone_order: number;
  milestone_name: string;
  milestone_description?: string;
  percentage_of_total?: number;
  fixed_amount?: number;
  calculated_amount: number;
  completion_criteria?: string;
  documentation_required?: string[];
  status: MilestoneStatus;
  completed_at?: string;
  verified_at?: string;
  payment_status?: string;
}

export interface NegotiationHistoryItem {
  id: number;
  service_referral_id: number;
  proposal_id?: number;
  event_type: string;
  event_description?: string;
  actor_type?: string;
  actor_id?: number;
  actor_name?: string;
  proposed_amount?: number;
  previous_amount?: number;
  amount_change?: number;
  created_at: string;
}

export interface ProviderRateCard {
  id: number;
  provider_type: string;
  provider_id: number;
  service_type_code: string;
  standard_rate?: number;
  urgent_rate?: number;
  emergent_rate?: number;
  preferred_pricing_model: PricingModel;
  accepts_milestone_based: boolean;
  accepts_hourly: boolean;
  max_active_referrals: number;
  current_active_referrals: number;
  allows_negotiation: boolean;
  min_acceptable_rate?: number;
  max_discount_percentage?: number;
  effective_from: string;
  effective_until?: string;
  is_active: boolean;
}

export interface CreateProposalRequest {
  service_referral_id: number;
  provider_type: string;
  provider_id: number | string | undefined;
  pricing_model?: PricingModel;
  base_rate?: number;
  milestones?: Omit<PricingMilestone, 'id' | 'status'>[];
  hourly_rate?: number;
  estimated_hours?: number;
  adjustments?: PricingAdjustment[];
  valid_days?: number;
}

export interface CounterProposalRequest {
  new_amount: number;
  counter_reason: string;
  milestones?: Omit<PricingMilestone, 'id' | 'status'>[];
}

export interface ProposalResponseRequest {
  response_notes?: string;
}

export interface CompleteMilestoneRequest {
  notes?: string;
}

export interface VerifyMilestoneRequest {
  notes?: string;
}

// ============================================================================
// API CLIENT
// ============================================================================

class PricingProposalsAPI {
  // ========================================================================
  // PROPOSAL CRUD
  // ========================================================================

  /**
   * Get proposal by ID
   */
  async getProposal(proposalId: number): Promise<PricingProposal> {
    const response = await apiClient.get<PricingProposal>(`/api/pricing-proposals/${proposalId}`);
    return response.data;
  }

  /**
   * List proposals for a referral
   */
  async getProposalsForReferral(
    referralId: number,
    params?: {
      status?: ProposalStatus;
      include_superseded?: boolean;
    }
  ): Promise<PricingProposal[]> {
    const response = await apiClient.get<PricingProposal[]>(
      `/api/pricing-proposals/referral/${referralId}`,
      { params }
    );
    return response.data;
  }

  /**
   * Get active proposals for the current provider
   */
  async getProviderProposals(params?: {
    status?: ProposalStatus;
    page?: number;
    page_size?: number;
  }): Promise<PricingProposal[]> {
    const response = await apiClient.get<PricingProposal[]>('/api/pricing-proposals/provider', {
      params,
    });
    return response.data;
  }

  /**
   * Create a new proposal
   */
  async createProposal(data: CreateProposalRequest): Promise<PricingProposal> {
    const response = await apiClient.post<PricingProposal>('/api/pricing-proposals', data);
    return response.data;
  }

  /**
   * Auto-create initial proposal based on rate cards
   */
  async createInitialProposal(
    referralId: number,
    providerType: string,
    providerId: number
  ): Promise<PricingProposal> {
    const response = await apiClient.post<PricingProposal>('/api/pricing-proposals/create-initial', {
      referral_id: referralId,
      provider_type: providerType,
      provider_id: providerId,
    });
    return response.data;
  }

  // ========================================================================
  // PROPOSAL WORKFLOW
  // ========================================================================

  /**
   * Submit a draft proposal
   */
  async submitProposal(proposalId: number): Promise<PricingProposal> {
    const response = await apiClient.post<PricingProposal>(
      `/api/pricing-proposals/${proposalId}/submit`
    );
    return response.data;
  }

  /**
   * Submit a counter proposal
   */
  async submitCounterProposal(
    proposalId: number,
    data: CounterProposalRequest
  ): Promise<PricingProposal> {
    const response = await apiClient.post<PricingProposal>(
      `/api/pricing-proposals/${proposalId}/counter`,
      data
    );
    return response.data;
  }

  /**
   * Accept a proposal
   */
  async acceptProposal(proposalId: number, data?: ProposalResponseRequest): Promise<PricingProposal> {
    const response = await apiClient.post<PricingProposal>(
      `/api/pricing-proposals/${proposalId}/accept`,
      data || {}
    );
    return response.data;
  }

  /**
   * Reject a proposal
   */
  async rejectProposal(proposalId: number, data?: ProposalResponseRequest): Promise<PricingProposal> {
    const response = await apiClient.post<PricingProposal>(
      `/api/pricing-proposals/${proposalId}/reject`,
      data || {}
    );
    return response.data;
  }

  // ========================================================================
  // MILESTONES
  // ========================================================================

  /**
   * Get milestones for a proposal
   */
  async getMilestones(proposalId: number): Promise<PricingMilestoneRecord[]> {
    const response = await apiClient.get<PricingMilestoneRecord[]>(
      `/api/pricing-proposals/${proposalId}/milestones`
    );
    return response.data;
  }

  /**
   * Get milestone payment status for a referral
   */
  async getMilestonePaymentStatus(referralId: number): Promise<PricingMilestoneRecord[]> {
    const response = await apiClient.get<PricingMilestoneRecord[]>(
      `/api/pricing-proposals/referral/${referralId}/milestone-status`
    );
    return response.data;
  }

  /**
   * Complete a milestone
   */
  async completeMilestone(
    milestoneId: number,
    data?: CompleteMilestoneRequest
  ): Promise<PricingMilestoneRecord> {
    const response = await apiClient.post<PricingMilestoneRecord>(
      `/api/pricing-proposals/milestones/${milestoneId}/complete`,
      data || {}
    );
    return response.data;
  }

  /**
   * Verify a completed milestone
   */
  async verifyMilestone(
    milestoneId: number,
    data?: VerifyMilestoneRequest
  ): Promise<PricingMilestoneRecord> {
    const response = await apiClient.post<PricingMilestoneRecord>(
      `/api/pricing-proposals/milestones/${milestoneId}/verify`,
      data || {}
    );
    return response.data;
  }

  // ========================================================================
  // NEGOTIATION HISTORY
  // ========================================================================

  /**
   * Get negotiation history for a referral
   */
  async getNegotiationHistory(referralId: number): Promise<NegotiationHistoryItem[]> {
    const response = await apiClient.get<NegotiationHistoryItem[]>(
      `/api/pricing-proposals/referral/${referralId}/history`
    );
    return response.data;
  }

  // ========================================================================
  // RATE CARDS
  // ========================================================================

  /**
   * Get rate cards for a provider
   */
  async getProviderRateCards(
    providerType: string,
    providerId: number
  ): Promise<ProviderRateCard[]> {
    const response = await apiClient.get<ProviderRateCard[]>(
      `/api/pricing-proposals/rate-cards/${providerType}/${providerId}`
    );
    return response.data;
  }

  /**
   * Get rate card for a specific service
   */
  async getRateCardForService(
    providerType: string,
    providerId: number | string | undefined,
    serviceTypeCode: string
  ): Promise<ProviderRateCard | null> {
    const response = await apiClient.get<ProviderRateCard | null>(
      `/api/pricing-proposals/rate-cards/${providerType}/${providerId}/${serviceTypeCode}`
    );
    return response.data;
  }
}

// Export singleton instance
export const pricingProposalsAPI = new PricingProposalsAPI();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function getProposalStatusLabel(status: ProposalStatus): string {
  const labels: Record<ProposalStatus, string> = {
    draft: 'Draft',
    submitted: 'Pending Review',
    accepted: 'Accepted',
    rejected: 'Rejected',
    expired: 'Expired',
    superseded: 'Superseded',
  };
  return labels[status] || status;
}

export function getProposalStatusColor(status: ProposalStatus): string {
  const colors: Record<ProposalStatus, string> = {
    draft: 'gray',
    submitted: 'yellow',
    accepted: 'green',
    rejected: 'red',
    expired: 'gray',
    superseded: 'gray',
  };
  return colors[status] || 'gray';
}

export function getMilestoneStatusLabel(status: MilestoneStatus): string {
  const labels: Record<MilestoneStatus, string> = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    verified: 'Verified',
    paid: 'Paid',
  };
  return labels[status] || status;
}

export function getMilestoneStatusColor(status: MilestoneStatus): string {
  const colors: Record<MilestoneStatus, string> = {
    pending: 'gray',
    in_progress: 'blue',
    completed: 'yellow',
    verified: 'green',
    paid: 'purple',
  };
  return colors[status] || 'gray';
}

export function getPricingModelLabel(model: PricingModel): string {
  const labels: Record<PricingModel, string> = {
    per_service: 'Per Service',
    milestone_based: 'Milestone Based',
    hourly: 'Hourly',
    package: 'Package',
  };
  return labels[model] || model;
}

export function formatCurrency(amount: number | undefined | null, currency = 'USD'): string {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function calculateMilestoneTotal(milestones: PricingMilestone[]): number {
  return milestones.reduce((sum, m) => sum + (m.amount || 0), 0);
}

export function validateMilestonePercentages(milestones: PricingMilestone[]): boolean {
  const total = milestones.reduce((sum, m) => sum + (m.percentage || 0), 0);
  return Math.abs(total - 100) < 0.01; // Allow for floating point errors
}

export default pricingProposalsAPI;
